import React, { useMemo, useEffect, useState } from "react";
import {
  GoogleMap,
  Marker,
  DirectionsRenderer,
  useJsApiLoader,
} from "@react-google-maps/api";
import { MapAPIKey } from "../../Utility/Utility";
import Styles from "./MapContainer.module.css";

function MapContainer({ origin, location, showDirections = false }) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: MapAPIKey,
    libraries: ["places"],
  });

  const [directions, setDirections] = useState(null);

  const destination = useMemo(
    () => ({ lat: location.lat, lng: location.lng }),
    [location],
  );

  const center = useMemo(() => {
    return origin ?? destination;
  }, [origin, destination]);

  useEffect(() => {
    // Clear directions when disabled or origin missing
    if (!showDirections || !origin) {
      setDirections(null);
      return;
    }

    if (!isLoaded) return;

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin,
        destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);
        } else {
          console.error("Directions failed:", status);
          setDirections(null);
        }
      },
    );
  }, [isLoaded, origin, destination, showDirections]);

  if (!isLoaded) return null;

  return (
    <GoogleMap
      mapContainerClassName={Styles.mapContainer}
      center={center}
      zoom={16}
    >
      {/* Destination marker (always visible) */}
      <Marker position={destination} />

      {/* Origin marker (only when origin exists) */}
      {origin && <Marker position={origin} />}

      {/* Directions (only when enabled) */}
      {showDirections && origin && directions && (
        <DirectionsRenderer
          directions={directions}
          options={{
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: "#2684ff",
              strokeWeight: 5,
            },
          }}
        />
      )}
    </GoogleMap>
  );
}

export default React.memo(MapContainer);
